import React, { useState } from "react";
import { Instagram, CheckCircle2, AlertTriangle, Loader2, Clock, Copy } from "lucide-react";
import { Btn } from "../plan/shared";
import { IgSetupGuide } from "./IgSetupGuide";
import { studioApi, errMsg, IG_REDIRECT } from "../../lib/studioApi";

const input = "bg-[#0b0c10] border border-zinc-800 focus:border-[#BEFF38] rounded-lg px-3 py-2.5 text-white text-sm font-mono outline-none w-full";

const RedirectUri = ({ notify }) => (
  <div className="grid gap-1" data-testid="ig-redirect-uri">
    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">OAuth redirect URI to add in Meta</span>
    <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-black/40 px-3 py-2">
      <code className="text-[11px] text-[#BEFF38] break-all flex-1">{IG_REDIRECT}</code>
      <button onClick={() => navigator.clipboard.writeText(IG_REDIRECT).then(() => notify("Redirect URI copied"))} aria-label="Copy redirect URI" data-testid="ig-redirect-copy" className="text-zinc-400 hover:text-white"><Copy className="w-4 h-4" /></button>
    </div>
  </div>
);

const TokenFallback = ({ onChange, notify }) => {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const st = await studioApi.igToken(token.trim());
      onChange(st);
      notify(`Connected to @${st.username}`);
    } catch (e) {
      notify(errMsg(e), "destructive");
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="text-xs" data-testid="ig-token-fallback">
      <summary className="cursor-pointer text-zinc-500 hover:text-white">Or paste a token from Meta's "Generate token" button</summary>
      <div className="mt-2 flex gap-2">
        <input className={input} type="password" placeholder="IGAA…" value={token} onChange={(e) => setToken(e.target.value)} data-testid="ig-token-input" />
        <Btn variant="ghost" onClick={save} disabled={busy || token.trim().length < 20} data-testid="ig-token-save-btn">{busy ? "Saving…" : "Save"}</Btn>
      </div>
    </details>
  );
};

const Connect = ({ onChange, notify }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      window.location.href = (await studioApi.igOAuthStart()).authorization_url;
    } catch (e) {
      notify(errMsg(e), "destructive");
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-3">
      <IgSetupGuide />
      <RedirectUri notify={notify} />
      <Btn onClick={go} disabled={busy} className="self-start" data-testid="ig-connect-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />} {busy ? "Opening Instagram…" : "Connect with Instagram"}
      </Btn>
      <TokenFallback onChange={onChange} notify={notify} />
    </div>
  );
};

const expiry = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null);

export const IgConnectPanel = ({ status, onChange, notify }) => {
  if (!status) return null;
  const disconnect = async () => onChange(await studioApi.igDisconnect());
  return (
    <div data-testid="ig-status-card" className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 lg:p-5 grid gap-4">
      <div className="flex items-start gap-3">
        <Instagram className={`w-5 h-5 mt-0.5 ${status.connected ? "text-[#BEFF38]" : "text-zinc-500"}`} />
        <div className="text-sm min-w-0 flex-1">
          {status.connected ? (
            <p data-testid="ig-status-connected" className="text-white font-semibold flex items-center gap-1.5 flex-wrap">
              <CheckCircle2 className="w-4 h-4 text-[#BEFF38]" /> Auto-publish connected · @{status.username}
              <span className="text-zinc-500 text-xs font-normal flex items-center gap-1">
                <Clock className="w-3 h-3" /> scheduled posts go live {status.post_time}{status.expires_at ? ` · token auto-refreshes (valid to ${expiry(status.expires_at)})` : ""}
              </span>
            </p>
          ) : status.configured ? (
            <p data-testid="ig-status-error" className="text-amber-300 font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Instagram connection problem: {status.error}</p>
          ) : (
            <>
              <p data-testid="ig-status-manual" className="text-white font-semibold">Manual posting mode</p>
              <p className="text-zinc-500 text-xs mt-0.5">Connect your Instagram Business account to publish with one tap and auto-post scheduled content at {status.post_time}.</p>
            </>
          )}
        </div>
        {status.configured && status.source === "studio" && (
          <button onClick={disconnect} data-testid="ig-disconnect-btn" className="text-xs text-zinc-500 hover:text-red-400">Disconnect</button>
        )}
      </div>
      {!status.connected && <Connect onChange={onChange} notify={notify} />}
    </div>
  );
};
